import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import Stepper, { EStepState } from "@components/new/Stepper";
import { ButtonType } from "@components/new/Button";
import { Modal, ModalSize } from "@components/new/Modal";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import ConfirmModal from "@components/modals/ConfirmModal";
import {
  getFBPWarehouseStocks,
  removeMatchingWarehouse,
} from "@api/apiClient/fbp";
import Form from "./Form";
import { FormHandle, IYMInfoItem, IWBInfoItem } from "./types";
import AlertIcon from "@assets/icons/solid/circle-alert.svg?react";
import styles from "./Form.module.scss";

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
}

interface DelStoreModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  ymItems: IYMInfoItem[];
  wbItems: IWBInfoItem[];
  onError: () => void;
  onSuccess: () => void;
}

enum steps {
  INIT = "init",
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
  CONFIRM = "confirm",
}

const DelStoreModal = ({
  onClose,
  sellerWarehouseId,
  ymItems,
  wbItems,
  // onError: onDelError,
  onSuccess: onDelSuccess,
}: DelStoreModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.INIT);
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const [ headerMessage, setHeaderMessage ] = useState<React.ReactNode>();
  const formRef = useRef<FormHandle>({ submit: () => { } });

  const warehouseId = wbItems.length ? wbItems[0].id : ymItems[0]?.id ?? "";
  const marketplaceType = wbItems.length ? "Wildberries" : "Yandex";

  const warehouseStocksQuery = useQuery({
    queryKey: ["getFBPWarehouseStocks", marketplaceType, warehouseId, "total"],
    queryFn: () => getFBPWarehouseStocks({
      filters: [],
      page: { page: 1, pageSize: 1 },
      marketplaceType,
      warehouseId,
    }),
    retry: 1,
    enabled: Boolean(sellerWarehouseId && warehouseId),
  });

  useEffect(() => {
    if (!sellerWarehouseId || !warehouseId) {
      setStep(steps.ERROR);
    }
  }, [sellerWarehouseId, warehouseId]);

  useEffect(() => {
    if (!warehouseStocksQuery.isFetched) return;

    if (!warehouseStocksQuery.data?.items?.length) {
      setStep(steps.CONFIRM);
    } else {
      setStep(steps.FORM);
    }
  }, [warehouseStocksQuery.data, warehouseStocksQuery.isFetched]);

  useEffect(() => {
    if (warehouseStocksQuery.isError) {
      setStep(steps.ERROR);
    }
  }, [warehouseStocksQuery.isError]);

  const handleStatusChange = (props: ISubmitButtonProps, msg: React.ReactNode) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
    });
    setHeaderMessage(msg);
  }

  const onSuccess = () => {
    setStep(steps.SUCCESS);
    // stocksQuery.refetch();
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const stepsData = [
    { text: t('fbpPage.delStoreModal.step-1'), state: step === steps.FORM ? EStepState.ENABLED : EStepState.SUCCESS },
    {
      text: t('fbpPage.delStoreModal.step-2'),
      state: (step === steps.SUCCESS ? EStepState.SUCCESS : (
        step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
      )),
    },
  ]

  if (step === steps.INIT) return null

  if (step === steps.CONFIRM) {
    return (
      <ConfirmModal
        title={t("fbpPage.delStoreModal.confirmTitle")}
        description={t("fbpPage.delStoreModal.confirmDescription")}
        noBtnTitle={t("fbpPage.delStoreModal.cancelBtn")}
        yesBtnTitle={t("fbpPage.delStoreModal.delBtn")}
        icon={<AlertIcon />}
        onClose={onClose}
        requestFn={() => removeMatchingWarehouse({ id: warehouseId, marketplaceType })}
        onSuccess={() => {
          onDelSuccess()
        }}
        type="error"
        inModal
        yesBtnType={ButtonType.ATTENTION}
      />
    );
  }

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.delStoreModal.title')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.delStoreModal.closeBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          className={styles.successContent}
          descriptionClassName={styles.successDescription}
          title={t('fbpPage.delStoreModal.successTitle')}
          description={t('fbpPage.delStoreModal.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.delStoreModal.title')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.delStoreModal.closeBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.delStoreModal.repeatBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.delStoreModal.errorTitle')}
          description={t('fbpPage.delStoreModal.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.delStoreModal.title')}
      headerContent={<Stepper steps={stepsData} gap="small" />}
      headerTitleContent={headerMessage}
      size={ModalSize.L}
      onClose={onClose}
      confirmCancel
      confirmCancelDescription={t('fbpPage.delStoreModal.closeConfirmDescription')}
      footerButtons={[
        {
          title: t('fbpPage.delStoreModal.closeBtn'),
        },
        {
          title: t('fbpPage.delStoreModal.nextBtn'),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      <Form
        formRef={formRef}
        sellerWarehouseId={sellerWarehouseId}
        warehouseId={warehouseId}
        marketplaceType={marketplaceType}
        onStatusChange={handleStatusChange}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Modal>
  )
}

export default DelStoreModal;
